import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ApprovalRequest,
  useApproveStepMutation,
  useRejectStepMutation,
} from '../../../services/ApprovalService';

export type ApprovalAction = 'approve' | 'reject';

export interface UseApprovalActionsResult {
  comment: string;
  setComment: (value: string) => void;
  pendingAction: ApprovalAction | null;
  setPendingAction: (value: ApprovalAction | null) => void;
  actionError: string | null;
  snack: string | null;
  setSnack: (value: string | null) => void;
  submitting: boolean;
  commentMissing: boolean;
  runAction: (action: ApprovalAction) => Promise<void>;
}

export function useApprovalActions(approval: ApprovalRequest): UseApprovalActionsResult {
  const navigate = useNavigate();
  const [approveStep, approveState] = useApproveStepMutation();
  const [rejectStep, rejectState] = useRejectStepMutation();

  const [comment, setComment] = useState('');
  const [pendingAction, setPendingAction] = useState<ApprovalAction | null>(null);
  const [snack, setSnack] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const submitting = approveState.isLoading || rejectState.isLoading;
  const commentMissing = approval.requireComment && !comment.trim();

  const runAction = async (action: ApprovalAction) => {
    setActionError(null);
    const payload = {
      instanceId: approval.workflowInstanceId,
      stepId: approval.stepId,
      approvalRequestId: approval.id,
      comment: comment.trim() || undefined,
    };
    try {
      if (action === 'approve') {
        await approveStep(payload).unwrap();
        setSnack('Approval recorded.');
      } else {
        await rejectStep(payload).unwrap();
        setSnack('Rejection recorded.');
      }
      setPendingAction(null);
      navigate('/approvals');
    } catch (e) {
      const httpStatus = (e as { status?: number })?.status;
      const msg = (e as { data?: string })?.data;
      setActionError(
        httpStatus === 403
          ? 'You are not authorized to act on this approval.'
          : typeof msg === 'string' && msg
            ? msg
            : 'Action failed. Please try again.'
      );
    }
  };

  return {
    comment, setComment,
    pendingAction, setPendingAction,
    actionError, snack, setSnack,
    submitting, commentMissing, runAction,
  };
}
